"use client";

import { useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function PullToRefreshWrapper() {
  const theme = useTheme();
  const [refreshing, setRefreshing] = useState(false);
  const [pullHeight, setPullHeight] = useState(0);
  const startYRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const threshold = 80; // 새로고침 트리거 높이
  const maxPullHeight = 100; // 최대 풀 높이
  const releaseHeight = 50; // 릴리스 시 돌아갈 높이

  const handleTouchStart = (e: TouchEvent) => {
    if (window.scrollY === 0 && !refreshing) {
      startYRef.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (startYRef.current === null || refreshing) return;

    const deltaY = e.touches[0].clientY - startYRef.current;
    if (deltaY > 0) {
      e.preventDefault();
      // 비선형 저항 효과: 초기엔 빠르게, 나중엔 느리게
      const resistance = 1 - Math.min(deltaY / (maxPullHeight * 2), 0.5);
      const adjustedHeight = Math.min(deltaY * resistance, maxPullHeight);

      // requestAnimationFrame으로 부드러운 업데이트
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setPullHeight(adjustedHeight);
      });
    }
  };

  const handleTouchEnd = () => {
    if (refreshing) return;

    if (pullHeight >= threshold) {
      setRefreshing(true);
      setPullHeight(releaseHeight); // 릴리스 시 45px로 부드럽게 전환
      setTimeout(() => {
        window.location.reload();
      }, 600); // 애니메이션과 새로고침 타이밍 조정
    } else {
      setPullHeight(0); // 부드럽게 0으로 리셋
    }
    startYRef.current = null;
  };

  useEffect(() => {
    document.addEventListener("touchstart", handleTouchStart, { passive: false });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [refreshing, pullHeight]);

  return (
    <Box sx={{ overflow: "hidden" }}>
      {(pullHeight > 20 || refreshing) && (
        <Box
          sx={{
            height: pullHeight,
            backgroundColor: theme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "height 0.4s ease-out", // 부드러운 전환, ease-out으로 감속
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: theme.palette.background.default,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: refreshing ? "spin 0.8s linear infinite" : "none",
              transform: `rotate(${pullHeight * 3.6}deg)`, // 풀링 중 회전 효과
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            <RefreshIcon
              sx={{
                fontSize: 32,
                color: theme.palette.primary.light,
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
